import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useSelector } from 'react-redux';
import useIsMobile from '../../hooks/useIsMobile';
import {
  isIosDevice,
  isStandaloneDisplay,
  promptPwaInstall,
  subscribeInstallPrompt,
  getDeferredInstallPrompt,
} from '../../utils/pwaInstall';

const DISMISS_KEY = 'tg_pwa_install_dismissed_v1';

/**
 * Prompts testers (on mobile browsers) to install TestGate as a PWA.
 * @param {{ forceShow?: boolean }} props - when false, hide (shell priority)
 */
export default function PwaInstallBanner({ forceShow = true }) {
  const { user } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();
  const [canPrompt, setCanPrompt] = useState(Boolean(getDeferredInstallPrompt()));
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'tester' || !isMobile) return undefined;
    if (isStandaloneDisplay()) return undefined;
    if (localStorage.getItem(DISMISS_KEY) === '1') return undefined;

    const unsub = subscribeInstallPrompt(() => {
      setCanPrompt(true);
      setVisible(true);
      setIosHint(false);
    });

    if (isIosDevice() && !window.navigator.standalone) {
      setIosHint(true);
      setVisible(true);
    } else if (getDeferredInstallPrompt()) {
      setVisible(true);
    }

    return unsub;
  }, [user, isMobile]);

  if (!visible || !forceShow) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    const result = await promptPwaInstall();
    if (result.ok || result.reason === 'unavailable') {
      if (result.ok) setVisible(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: '#0f172a',
        color: '#fff',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
      }}
    >
      <GetAppIcon sx={{ mt: 0.25, color: '#38bdf8', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.35 }}>
          Install TestGate on your phone
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.45 }}>
          {iosHint && !canPrompt
            ? 'In Safari: tap Share → Add to Home Screen for a full-screen app experience.'
            : 'Add the tester app to your home screen — faster access to cycles, defects, and support chat.'}
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={install}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#cc0000' },
          }}
        >
          {canPrompt ? 'Install app' : 'Install tips'}
        </Button>
      </Box>
      <IconButton size="small" onClick={dismiss} aria-label="Dismiss" sx={{ color: '#94a3b8' }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
